import React from "react";
import clsx from "clsx";
import { InformationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Toggle, ToolTip, Button as AquiloButton } from "@truevault/aquilo";
import { useField, useFormState } from "react-final-form";
import { ConsentManagementVariant } from "../../../../../../common/service/server/dto/CMPSettingsDto";
import { ConsentSettingRow } from "../../../consentBanner/ConsentSettingRow";
import { ConsentRadio } from "../../../consentBanner/ConsentRadio";
import { ConsentTable } from "../../../consentBanner/ConsentTable";

export const VARIANT_TOOLTIPS = {
  informational:
    "Provides notice to visitors that their data is collected on your site. It does not allow visitors to control cookie consent. Banner text can be customized below.",
  optOut:
    "Automatically opts visitors into cookie tracking and data sharing, but gives them the ability to manage and remove consent.",
  optIn:
    "Requires visitor consent before allowing any unnecessary tracking cookies or data sharing with third parties.",
};

const MINORS_FIELD = "usConsentManagementVariantMinors";
const MINORS_DISABLED_REASON =
  "Consent is required because your Business Survey responses show that you collect data from minors.";

interface VariantOption {
  value: ConsentManagementVariant;
  label: string;
  tooltip?: string;
}

interface ConsentManagementVariantRowProps {
  title: string;
  helpText: React.ReactNode;
  fields: string[];
  iconPath?: string;
  allowInformationalBanner: boolean;
  allowOptOutBanner: boolean;
  allowOptInBanner: boolean;
  showTooltip?: boolean;
  fieldToLabel?: Record<string, string>;
  reverseOptionOrder?: boolean;
  fieldDescriptionToRowLabelProvider?: (
    field: string,
  ) => React.ReactNode /* provides the content of the "See states" hover for the potentially-overridable state list */;
  // Optional row-level on/off toggle (Global consent)
  rowToggled?: boolean;
  onRowToggle?: (nextChecked: boolean) => void;
  // Optional content between the help text and the table
  contentBeforeTable?: React.ReactNode;
  // Optional content under the table (state overrides, notes)
  contentAfterTable?: React.ReactNode;
  // Rendered flush against the table's bottom edge, e.g. a joined overrides table.
  tableAttachment?: React.ReactNode;
  // Header for the table's first column (defaults to "Region")
  tableHeader?: React.ReactNode;
  // Force table layout even with a single field (otherwise renders an on/off toggle or radios)
  forceTableLayout?: boolean;
  // Join this table's bottom edge to a sibling table directly below it.
  joinedBottom?: boolean;
  // Append an empty trailing column whose width matches the trash button on the sibling override table.
  // Keeps the radio columns horizontally aligned across the two tables.
  tableTrailingPlaceholder?: boolean;
  // Optional inline node placed immediately after the title text (e.g. an info icon w/ tooltip).
  titleTrailingAdornment?: React.ReactNode;
  // What "on" means for an on/off row, e.g. "Opt-in banner".
  onStateLabel?: string;
}

const OptionHeader = ({ option, showTooltip }: { option: VariantOption; showTooltip: boolean }) => (
  <div className="flex w-full items-center justify-center gap-[4px] whitespace-nowrap">
    <span>{option.label}</span>
    {showTooltip && option.tooltip && (
      <ToolTip text={option.tooltip} maxWidth="400px">
        <InformationCircleIcon
          className="h-[16px] w-[16px] cursor-pointer text-neutral-500"
          aria-label={`About ${option.label}`}
        />
      </ToolTip>
    )}
  </div>
);

export const ConsentManagementVariantRow = ({
  title,
  helpText,
  fields,
  fieldToLabel,
  iconPath,
  allowInformationalBanner,
  allowOptOutBanner,
  allowOptInBanner,
  showTooltip = false,
  reverseOptionOrder = false,
  fieldDescriptionToRowLabelProvider = null,
  rowToggled,
  onRowToggle,
  contentBeforeTable,
  contentAfterTable,
  tableAttachment,
  tableHeader = "Region",
  forceTableLayout = false,
  joinedBottom = false,
  tableTrailingPlaceholder = false,
  titleTrailingAdornment,
  onStateLabel,
}: ConsentManagementVariantRowProps) => {
  const options: VariantOption[] = [{ value: ConsentManagementVariant.NONE, label: "None" }];
  if (allowInformationalBanner) {
    options.push({
      value: ConsentManagementVariant.INFORMATIONAL_ONLY,
      label: "Informational",
      tooltip: VARIANT_TOOLTIPS.informational,
    });
  }
  if (allowOptOutBanner) {
    options.push({
      value: ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
      label: "Opt-out",
      tooltip: VARIANT_TOOLTIPS.optOut,
    });
  }
  if (allowOptInBanner) {
    options.push({
      value: ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
      label: "Opt-in",
      tooltip: VARIANT_TOOLTIPS.optIn,
    });
  }
  if (reverseOptionOrder) {
    options.reverse();
  }

  // A single field with exactly one "on" option is a simple on/off choice.
  const isSingleField = fields?.length === 1 && !forceTableLayout;
  const nonNoneOptions = options.filter((o) => o.value !== ConsentManagementVariant.NONE);
  const isBinaryChoice = isSingleField && nonNoneOptions.length === 1;

  const singleFieldApi = useField(fields[0]);
  const { values } = useFormState<{ [key: string]: any }>({ subscription: { values: true } });
  const isValueUnset = (v: unknown) => v === undefined || v === null || v === "";

  const titleContent = (
    <span className="inline-flex items-center gap-[8px]">
      {iconPath && <img src={iconPath} alt="" width="22" height="15" className="rounded-[2px]" />}
      <span>{title}</span>
      {titleTrailingAdornment}
    </span>
  );

  if (isBinaryChoice) {
    const onVariant = nonNoneOptions[0].value;
    const checked =
      Boolean(singleFieldApi.input.value) &&
      singleFieldApi.input.value !== ConsentManagementVariant.NONE;

    return (
      <ConsentSettingRow title={titleContent} helpText={helpText}>
        <div className="flex flex-wrap items-center gap-[10px]">
          <Toggle
            checked={checked}
            label={`${title} banner`}
            onChange={(nextChecked) =>
              singleFieldApi.input.onChange(nextChecked ? onVariant : ConsentManagementVariant.NONE)
            }
          />
          <span className="text-[14px] font-medium leading-[20px] text-neutral-800">
            {checked ? (onStateLabel ? `On · ${onStateLabel}` : "On") : "Off"}
          </span>
          {!checked && (
            <span className="inline-flex h-[22px] items-center rounded-full bg-primary-100 px-[9px] text-[12px] font-medium leading-[16px] text-primary-700">
              On is recommended
            </span>
          )}
        </div>
      </ConsentSettingRow>
    );
  }

  if (isSingleField) {
    return (
      <ConsentSettingRow title={titleContent} helpText={helpText}>
        <div
          role="radiogroup"
          aria-label={title}
          className="flex flex-wrap items-center gap-x-[20px] gap-y-[10px]"
        >
          {options.map((option) => (
            <label
              key={option.value}
              className="inline-flex cursor-pointer items-center gap-[8px] text-[14px] leading-[20px] text-neutral-800"
            >
              <ConsentRadio field={fields[0]} value={option.value} label={option.label} />
              {option.label}
            </label>
          ))}
        </div>
      </ConsentSettingRow>
    );
  }

  const showTable = rowToggled !== false;

  return (
    <ConsentSettingRow title={titleContent} helpText={helpText} layout="stacked">
      {onRowToggle && (
        <div className="flex items-center gap-[10px]">
          <Toggle checked={Boolean(rowToggled)} label={`${title} banner`} onChange={onRowToggle} />
          <span className="text-[14px] font-medium leading-[20px] text-neutral-800">
            {rowToggled ? "On" : "Off"}
          </span>
        </div>
      )}
      {showTable && contentBeforeTable}
      {showTable && (
        <div className="w-full">
          <ConsentTable<string>
            joinedBottom={joinedBottom}
            keyExtractor={(fieldName) => fieldName}
            columns={[
              {
                key: "label",
                header: tableHeader,
                className: "pr-[12px] font-medium",
                render: (fieldName) => {
                  const baseLabel = fieldDescriptionToRowLabelProvider
                    ? fieldDescriptionToRowLabelProvider(fieldName)
                    : fieldToLabel[fieldName];
                  return (
                    <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[2px]">
                      <span className="whitespace-nowrap">{baseLabel}</span>
                      {isValueUnset(values?.[fieldName]) && (
                        <span className="whitespace-nowrap text-[12px] font-medium leading-[16px] text-red-600">
                          Choose a banner
                        </span>
                      )}
                    </div>
                  );
                },
              },
              ...options.map((option) => ({
                key: option.value,
                header: <OptionHeader option={option} showTooltip={showTooltip} />,
                className: clsx(
                  "pl-[8px] pr-[8px] text-center",
                  tableTrailingPlaceholder && "w-[120px]",
                ),
                render: (fieldName: string) => {
                  const isDisabled =
                    fieldName === MINORS_FIELD &&
                    (option.value === ConsentManagementVariant.INFORMATIONAL_ONLY ||
                      option.value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT);
                  const rowLabel = fieldToLabel?.[fieldName] ?? fieldName;

                  return (
                    <div className="flex justify-center">
                      <ConsentRadio
                        field={fieldName}
                        value={option.value}
                        label={`${rowLabel}: ${option.label}`}
                        disabled={isDisabled}
                        disabledReason={isDisabled ? MINORS_DISABLED_REASON : undefined}
                      />
                    </div>
                  );
                },
              })),
              ...(tableTrailingPlaceholder
                ? [
                    {
                      // Reserve the same horizontal space as the trash button column on the
                      // sibling override table so the radio columns line up across both.
                      // Render an invisible copy of that button to guarantee a pixel-perfect match.
                      key: "_trailing_placeholder",
                      header: null,
                      className: "w-[64px] pl-[8px] pr-[8px] text-center",
                      render: () => (
                        <div
                          className="flex items-center justify-center"
                          style={{ visibility: "hidden" }}
                          aria-hidden="true"
                        >
                          <AquiloButton
                            color="tertiary"
                            variant="round"
                            isCircular
                            onClick={() => {}}
                          >
                            <TrashIcon className="h-[20px] w-[20px] text-gray-500" />
                          </AquiloButton>
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
            data={fields}
          />
          {tableAttachment}
        </div>
      )}
      {showTable && contentAfterTable}
    </ConsentSettingRow>
  );
};
